import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';
import { nombreSeguroStorage } from './lib/nombre-storage';
import { useCompany } from './CompanyContext';
import { useImpersonation } from './ImpersonationContext';
import { validateFileSize } from './lib/validateFileSize';
import EnlaceArchivo from './components/EnlaceArchivo';
import SelectBuscable from './components/SelectBuscable';
import './LayoutPro.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Warehouse { id: string; name: string; }

interface ZoneFormItem {
  nombre:    string;
  largo:     string;
  ancho:     string;
  ubicacion: string;
}

export interface LayoutRequest {
  id:             string;
  company_id:     string;
  warehouse_id:   string | null;
  warehouse_name: string | null;
  dimensions:     Record<string, any>;
  status:         'pendiente' | 'en_proceso' | 'entregado';
  notes:          string | null;
  layout_url:     string | null;
  file_url:       string | null;
  created_at:     string;
  delivered_at:   string | null;
}

// ── Status config ─────────────────────────────────────────────────────────────

export const STATUS_CFG = {
  pendiente:  { label: 'Pendiente',  color: '#eab308', bg: 'rgba(234,179,8,0.12)',  border: 'rgba(234,179,8,0.3)'  },
  en_proceso: { label: 'En proceso', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)' },
  entregado:  { label: 'Entregado',  color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.3)'  },
} as const;

const EMPTY_ZONE: ZoneFormItem = { nombre: '', largo: '', ancho: '', ubicacion: '' };

// ── Component ─────────────────────────────────────────────────────────────────

export default function LayoutPro() {
  const { company } = useCompany();
  const { impersonated } = useImpersonation();
  const effectiveCompany   = impersonated ?? company;
  const effectiveCompanyId = effectiveCompany?.id ?? null;

  const [requests,    setRequests]    = useState<LayoutRequest[]>([]);
  const [warehouses,  setWarehouses]  = useState<Warehouse[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [toast,       setToast]       = useState('');

  // Form fields
  const [whId,               setWhId]               = useState('');
  const [largo,              setLargo]              = useState('');
  const [ancho,              setAncho]              = useState('');
  const [altura,             setAltura]             = useState('');
  const [puertasCount,       setPuertasCount]       = useState('1');
  const [puertasUbics,       setPuertasUbics]       = useState<string[]>([]);
  const [ventilacion,        setVentilacion]        = useState('');
  const [drenaje,            setDrenaje]            = useState('');
  const [contraIncendios,    setContraIncendios]    = useState('');
  const [orientacion,        setOrientacion]        = useState('');
  const [cargaPiso,          setCargaPiso]          = useState('');
  const [zonas,              setZonas]              = useState<ZoneFormItem[]>([{ ...EMPTY_ZONE }]);
  const [notas,              setNotas]              = useState('');
  const [attachFile,         setAttachFile]         = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (!effectiveCompanyId) { setLoading(false); return; }
    setLoading(true);
    const [{ data: reqs }, { data: whs }] = await Promise.all([
      supabase.from('layout_requests')
        .select('*')
        .eq('company_id', effectiveCompanyId)
        .order('created_at', { ascending: false }),
      supabase.from('warehouses')
        .select('id, name')
        .eq('company_id', effectiveCompanyId)
        .order('name'),
    ]);
    setRequests(reqs ?? []);
    setWarehouses(whs ?? []);
    setLoading(false);
  }, [effectiveCompanyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Form helpers ───────────────────────────────────────────────────────────

  const resetForm = () => {
    setWhId(''); setLargo(''); setAncho(''); setAltura('');
    setPuertasCount('1'); setPuertasUbics([]); setVentilacion('');
    setDrenaje(''); setContraIncendios(''); setOrientacion('');
    setCargaPiso(''); setZonas([{ ...EMPTY_ZONE }]); setNotas('');
    setAttachFile(null); setSubmitError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const togglePuerta = (val: string) =>
    setPuertasUbics(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    );

  const updateZona = (idx: number, field: keyof ZoneFormItem, val: string) =>
    setZonas(prev => prev.map((z, i) => i === idx ? { ...z, [field]: val } : z));

  const addZona    = () => setZonas(prev => [...prev, { ...EMPTY_ZONE }]);
  const removeZona = (idx: number) => setZonas(prev => prev.filter((_, i) => i !== idx));

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveCompanyId) return;
    if (!largo || !ancho || !altura) { setSubmitError('Las dimensiones (largo, ancho, altura) son obligatorias.'); return; }
    if (attachFile) {
      const sizeError = validateFileSize(attachFile, 20);
      if (sizeError) { setSubmitError(sizeError); return; }
    }

    setSubmitting(true);
    setSubmitError('');

    let fileUrl: string | null = null;
    if (attachFile) {
      const ext  = attachFile.name.split('.').pop() ?? 'bin';
      const path = `${effectiveCompanyId}/requests/${nombreSeguroStorage(`${Date.now()}.${ext}`)}`;
      const { error: upErr } = await supabase.storage
        .from('layout-files')
        .upload(path, attachFile, { upsert: false });
      if (upErr) { setSubmitError('Error al subir archivo: ' + upErr.message); setSubmitting(false); return; }
      const { data: pub } = supabase.storage.from('layout-files').getPublicUrl(path);
      fileUrl = pub.publicUrl;
    }

    const selectedWh = warehouses.find(w => w.id === whId);
    const dimensions = {
      largo:               parseFloat(largo) || 0,
      ancho:               parseFloat(ancho) || 0,
      altura:              parseFloat(altura) || 0,
      puertas_count:       parseInt(puertasCount) || 1,
      puertas_ubicaciones: puertasUbics,
      ventilacion:         ventilacion || null,
      drenaje:             drenaje === 'si' ? true : drenaje === 'no' ? false : null,
      contra_incendios:    contraIncendios || null,
      orientacion:         orientacion || null,
      carga_piso:          cargaPiso ? parseFloat(cargaPiso) : null,
      zonas:               zonas
        .filter(z => z.nombre.trim())
        .map(z => ({
          nombre:    z.nombre.trim(),
          largo:     parseFloat(z.largo) || 0,
          ancho:     parseFloat(z.ancho) || 0,
          ubicacion: z.ubicacion.trim(),
        })),
    };

    const { error } = await supabase.from('layout_requests').insert({
      company_id:     effectiveCompanyId,
      warehouse_id:   whId || null,
      warehouse_name: selectedWh?.name ?? null,
      dimensions,
      status:         'pendiente',
      notes:          notas.trim() || null,
      file_url:       fileUrl,
    });

    if (error) {
      setSubmitError('Error al enviar solicitud: ' + error.message);
      setSubmitting(false);
      return;
    }

    showToast('Solicitud enviada correctamente. Recibirás tu diseño en un plazo de 5 a 7 días hábiles.');
    resetForm();
    setShowForm(false);
    fetchData();
    setSubmitting(false);
  };

  // ── Render: list ──────────────────────────────────────────────────────────

  if (!showForm) {
    return (
      <div className="lp-root">
        <div className="lp-header">
          <div>
            <h3 className="lp-title">Layout Pro</h3>
            <p className="lp-subtitle">
              Solicita el diseño de distribución óptima de tu bodega según la NTE INEN 2266:2013.
              · {requests.length} solicitud{requests.length !== 1 ? 'es' : ''}
            </p>
          </div>
          <button className="lp-btn-new" onClick={() => { resetForm(); setShowForm(true); }}>
            <PlusIcon /> Nueva Solicitud
          </button>
        </div>

        {loading ? (
          <div className="lp-loading">
            <span className="lp-spinner" />
            <p>Cargando solicitudes…</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="lp-empty">
            <LayoutEmptyIcon />
            <p className="lp-empty-title">Sin solicitudes</p>
            <p className="lp-empty-sub">
              Envía tu primera solicitud y el equipo StorSafe preparará el layout óptimo de tu bodega, con zonas seguras según la NTE INEN 2266:2013.
            </p>
            <button className="lp-btn-new" onClick={() => { resetForm(); setShowForm(true); }}>
              <PlusIcon /> Crear primera solicitud
            </button>
          </div>
        ) : (
          <div className="lp-requests-list">
            {requests.map(req => {
              const cfg  = STATUS_CFG[req.status];
              const dims = req.dimensions as any;
              return (
                <div key={req.id} className="lp-request-card">
                  <div className="lp-card-header">
                    <div className="lp-card-title">
                      <LayoutIcon />
                      <span>{req.warehouse_name ?? 'Bodega no especificada'}</span>
                    </div>
                    <span className="lp-status-badge"
                      style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}
                    >
                      <span className="lp-status-dot" style={{ background: cfg.color }} />
                      {cfg.label}
                    </span>
                  </div>

                  {req.status === 'pendiente' && (
                    <p style={{ color: '#64748b', fontSize: '0.76rem', margin: '2px 0 0' }}>
                      Tiempo estimado de respuesta: 5-7 días hábiles
                    </p>
                  )}

                  <div className="lp-card-dims">
                    {dims.largo && (
                      <span>{dims.largo} × {dims.ancho} × {dims.altura} m</span>
                    )}
                    {dims.zonas?.length > 0 && (
                      <span>{dims.zonas.length} zona{dims.zonas.length !== 1 ? 's' : ''}</span>
                    )}
                    {dims.orientacion && <span>Orientación {dims.orientacion}</span>}
                    {dims.ventilacion && <span>Ventilación {dims.ventilacion}</span>}
                    {dims.contra_incendios && dims.contra_incendios !== 'ninguno' && (
                      <span>{dims.contra_incendios}</span>
                    )}
                  </div>

                  {req.notes && <p className="lp-card-notes">{req.notes}</p>}

                  {req.status === 'en_proceso' && (
                    <div className="lp-card-progress">
                      <ProgressIcon /> El equipo StorSafe está trabajando en tu diseño.
                    </div>
                  )}

                  <div className="lp-card-footer">
                    <span className="lp-card-date">
                      Enviada el {new Date(req.created_at).toLocaleDateString('es-CL', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                      {req.delivered_at && (
                        <> · Entregada el {new Date(req.delivered_at).toLocaleDateString('es-CL', {
                          day: 'numeric', month: 'short', year: 'numeric',
                        })}</>
                      )}
                    </span>
                    <div className="lp-card-actions">
                      {req.file_url && (
                        <EnlaceArchivo bucket="layout-files" valor={req.file_url} className="lp-btn-link">
                          <PaperclipIcon /> Plano adjunto
                        </EnlaceArchivo>
                      )}
                      {req.status === 'entregado' && req.layout_url && (
                        <EnlaceArchivo bucket="layout-files" valor={req.layout_url} descargar className="lp-btn-download">
                          <DownloadIcon /> Descargar Layout
                        </EnlaceArchivo>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {toast && <div className="lp-toast">{toast}</div>}
      </div>
    );
  }

  // ── Render: form ──────────────────────────────────────────────────────────

  const puertaLabels: Record<string, string> = {
    frente:            'Frente',
    fondo:             'Fondo',
    lateral_izquierdo: 'Lateral izq.',
    lateral_derecho:   'Lateral der.',
  };

  return (
    <div className="lp-root">
      <div className="lp-breadcrumb">
        <button className="lp-back-btn" onClick={() => setShowForm(false)}>
          <BackIcon /> Solicitudes
        </button>
        <span className="lp-bc-sep">/</span>
        <span className="lp-bc-current">Nueva Solicitud</span>
      </div>

      <form className="lp-form" onSubmit={handleSubmit}>

        {/* ── Sección 1: Datos de la bodega ── */}
        <div className="lp-section">
          <h4 className="lp-section-title"><LayoutIcon /> Datos de la Bodega</h4>

          <div className="lp-field">
            <label>Bodega existente</label>
            <SelectBuscable ariaLabel="Bodega existente" value={whId} onChange={setWhId} vacio="— Seleccionar bodega (opcional) —"
              opciones={warehouses.map(wh => ({ value: wh.id, label: wh.name }))} />
          </div>

          <div className="lp-row-3">
            <div className="lp-field">
              <label>Largo <span className="lp-req">*</span></label>
              <div className="lp-input-unit">
                <input type="number" min="1" step="0.1" placeholder="30"
                  value={largo} onChange={e => setLargo(e.target.value)} required />
                <span className="lp-unit">m</span>
              </div>
            </div>
            <div className="lp-field">
              <label>Ancho <span className="lp-req">*</span></label>
              <div className="lp-input-unit">
                <input type="number" min="1" step="0.1" placeholder="20"
                  value={ancho} onChange={e => setAncho(e.target.value)} required />
                <span className="lp-unit">m</span>
              </div>
            </div>
            <div className="lp-field">
              <label>Altura <span className="lp-req">*</span></label>
              <div className="lp-input-unit">
                <input type="number" min="1" step="0.1" placeholder="6"
                  value={altura} onChange={e => setAltura(e.target.value)} required />
                <span className="lp-unit">m</span>
              </div>
            </div>
          </div>

          <div className="lp-field" style={{ maxWidth: 140 }}>
            <label>Número de puertas</label>
            <input type="number" min="0" max="20"
              value={puertasCount} onChange={e => setPuertasCount(e.target.value)} />
          </div>

          <div className="lp-field">
            <label>Ubicación de puertas</label>
            <div className="lp-checkboxes">
              {Object.entries(puertaLabels).map(([val, lbl]) => (
                <label key={val}
                  className={`lp-checkbox-item ${puertasUbics.includes(val) ? 'checked' : ''}`}
                >
                  <input type="checkbox" checked={puertasUbics.includes(val)}
                    onChange={() => togglePuerta(val)} />
                  <span>{lbl}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="lp-row-2">
            <div className="lp-field">
              <label>Ventilación</label>
              <div className="lp-radios">
                {[['natural', 'Natural'], ['forzada', 'Forzada'], ['ambas', 'Ambas']].map(([v, l]) => (
                  <label key={v} className={`lp-radio-item ${ventilacion === v ? 'checked' : ''}`}>
                    <input type="radio" name="vent" checked={ventilacion === v} onChange={() => setVentilacion(v)} />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="lp-field">
              <label>¿Tiene drenaje?</label>
              <div className="lp-radios">
                {[['si', 'Sí'], ['no', 'No']].map(([v, l]) => (
                  <label key={v} className={`lp-radio-item ${drenaje === v ? 'checked' : ''}`}>
                    <input type="radio" name="drenaje" checked={drenaje === v} onChange={() => setDrenaje(v)} />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="lp-row-2">
            <div className="lp-field">
              <label>Sistema contra incendios</label>
              <div className="lp-radios lp-radios-wrap">
                {[['rociadores', 'Rociadores'], ['extintores', 'Extintores'], ['ambos', 'Ambos'], ['ninguno', 'Ninguno']].map(([v, l]) => (
                  <label key={v} className={`lp-radio-item ${contraIncendios === v ? 'checked' : ''}`}>
                    <input type="radio" name="ci" checked={contraIncendios === v} onChange={() => setContraIncendios(v)} />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="lp-field">
              <label>Orientación de la bodega</label>
              <div className="lp-radios lp-radios-wrap">
                {[['norte', 'Norte'], ['sur', 'Sur'], ['este', 'Este'], ['oeste', 'Oeste']].map(([v, l]) => (
                  <label key={v} className={`lp-radio-item ${orientacion === v ? 'checked' : ''}`}>
                    <input type="radio" name="orient" checked={orientacion === v} onChange={() => setOrientacion(v)} />
                    <span>{l}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="lp-field" style={{ maxWidth: 230 }}>
            <label>Capacidad de carga del piso (opcional)</label>
            <div className="lp-input-unit">
              <input type="number" min="0" step="50" placeholder="500"
                value={cargaPiso} onChange={e => setCargaPiso(e.target.value)} />
              <span className="lp-unit">kg/m²</span>
            </div>
          </div>
        </div>

        {/* ── Sección 2: Zonas ── */}
        <div className="lp-section">
          <div className="lp-section-header-row">
            <h4 className="lp-section-title"><ZoneIcon /> Zonas</h4>
            <button type="button" className="lp-btn-add-zone" onClick={addZona}>
              <PlusIcon /> Agregar zona
            </button>
          </div>

          {zonas.map((z, idx) => (
            <div key={idx} className="lp-zone-row">
              <div className="lp-zone-num">{idx + 1}</div>
              <div className="lp-zone-fields">
                <div className="lp-field lp-field-flex">
                  <label>Nombre</label>
                  <input type="text" placeholder="Zona A · Inflamables"
                    value={z.nombre} onChange={e => updateZona(idx, 'nombre', e.target.value)} />
                </div>
                <div className="lp-field lp-field-sm">
                  <label>Largo</label>
                  <div className="lp-input-unit">
                    <input type="number" min="0" step="0.1" placeholder="10"
                      value={z.largo} onChange={e => updateZona(idx, 'largo', e.target.value)} />
                    <span className="lp-unit">m</span>
                  </div>
                </div>
                <div className="lp-field lp-field-sm">
                  <label>Ancho</label>
                  <div className="lp-input-unit">
                    <input type="number" min="0" step="0.1" placeholder="8"
                      value={z.ancho} onChange={e => updateZona(idx, 'ancho', e.target.value)} />
                    <span className="lp-unit">m</span>
                  </div>
                </div>
                <div className="lp-field lp-field-flex">
                  <label>Ubicación en bodega</label>
                  <input type="text" placeholder="ej. esquina noreste"
                    value={z.ubicacion} onChange={e => updateZona(idx, 'ubicacion', e.target.value)} />
                </div>
              </div>
              {zonas.length > 1 && (
                <button type="button" className="lp-btn-remove-zone"
                  onClick={() => removeZona(idx)} title="Eliminar zona">
                  <TrashIcon />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* ── Sección 3: Info adicional ── */}
        <div className="lp-section">
          <h4 className="lp-section-title"><NoteIcon /> Información Adicional</h4>

          <div className="lp-field">
            <label>Observaciones adicionales</label>
            <textarea
              rows={4}
              placeholder="Describe condiciones especiales, requisitos normativos, restricciones de acceso, equipos fijos, etc."
              value={notas}
              onChange={e => setNotas(e.target.value)}
            />
          </div>

          <div className="lp-field">
            <label>Plano o foto existente (opcional)</label>
            <div className="lp-file-area" onClick={() => fileRef.current?.click()}>
              {attachFile ? (
                <>
                  <PaperclipIcon />
                  <span className="lp-file-name">{attachFile.name}</span>
                  <span className="lp-file-size">({(attachFile.size / 1024 / 1024).toFixed(1)} MB)</span>
                  <button
                    type="button"
                    className="lp-file-remove"
                    onClick={e => {
                      e.stopPropagation();
                      setAttachFile(null);
                      if (fileRef.current) fileRef.current.value = '';
                    }}
                  >
                    ✕
                  </button>
                </>
              ) : (
                <>
                  <UploadIcon />
                  <span>Haz clic para adjuntar imagen o PDF</span>
                  <span className="lp-file-hint">PNG, JPG, PDF · Máx. 10 MB</span>
                </>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files?.[0];
                if (!f) return;
                if (f.size > 10 * 1024 * 1024) { setSubmitError('El archivo no debe superar 10 MB.'); return; }
                setAttachFile(f);
                setSubmitError('');
              }}
            />
          </div>
        </div>

        {submitError && <p className="lp-error">{submitError}</p>}

        <div className="lp-form-footer">
          <button type="button" className="lp-btn-cancel" onClick={() => setShowForm(false)}>
            Cancelar
          </button>
          <button type="submit" className="lp-btn-submit" disabled={submitting}>
            {submitting && <span className="lp-spinner lp-spinner-sm" />}
            {submitting ? 'Enviando…' : 'Enviar Solicitud'}
          </button>
        </div>
      </form>

      {toast && <div className="lp-toast">{toast}</div>}
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
      <path d="M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

export function LayoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

function LayoutEmptyIcon() {
  return (
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ color: '#334155' }}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

function ZoneIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function NoteIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function PaperclipIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="16 16 12 12 8 16" />
      <line x1="12" y1="12" x2="12" y2="21" />
      <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="8 17 12 21 16 17" />
      <line x1="12" y1="12" x2="12" y2="21" />
      <path d="M20.88 18.09A5 5 0 0018 9h-1.26A8 8 0 103 16.29" />
    </svg>
  );
}

function ProgressIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
